import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import { useReportPermissionActions } from 'hooks/useReportPermissionActions';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AddIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { BsSearch } from 'react-icons/bs';
import { RxReset } from 'react-icons/rx';
import SearchTable2 from 'components/Tables/SearchTable2';
import { columnsDataReport, columnsDataReportPermission } from 'variables/columnsData';
import { Select as ReactSelect } from 'chakra-react-select';
import { useHistory, useParams } from 'react-router-dom';
import { checkForbidden } from 'helpers/checkForbidden';

const chakraStyles = {
  dropdownIndicator: (provided) => ({ ...provided, p: 0, w: '20px' }),
  container: (provided) => ({ ...provided, minWidth: '260px' }),
  menu: (provided) => ({ ...provided, zIndex: 15 }),
};

function DataTableReportPermissions() {
  const textColor = useColorModeValue('gray.700', 'white');
  const toast = useToast();
  const actions = useReportPermissionActions();
  const history = useHistory();
  const { report_id } = useParams();

  // --- Report list state ---
  const [reports, setReports] = useState([]);
  const [totalReports, setTotalReports] = useState(0);
  const [isFetchingReports, setIsFetchingReports] = useState(false);
  const [refreshReportPaging, setRefreshReportPaging] = useState(false);

  // --- Selected report / permissions state ---
  const [selectedReport, setSelectedReport] = useState(null);

  // Khi có report_id trên URL (refresh hoặc direct link), fetch report đó
  useEffect(() => {
    if (!report_id) return;
    actions.getReportById(report_id).then((res) => {
      if (res?.status === 200) setSelectedReport(res.data.data);
    });
  }, [report_id]);
  const [permissions, setPermissions] = useState([]);
  const [totalPermissions, setTotalPermissions] = useState(0);
  const [isFetchingPerms, setIsFetchingPerms] = useState(false);

  // --- Modal state ---
  const [isCreate, setIsCreate] = useState(true);
  const [currentPermId, setCurrentPermId] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // --- Search forms ---
  const {
    register: registerReportSearch,
    handleSubmit: handleSubmitReportSearch,
    watch: watchReportSearch,
    reset: resetReportSearch,
  } = useForm();

  const {
    register: registerPermSearch,
    handleSubmit: handleSubmitPermSearch,
    watch: watchPermSearch,
    reset: resetPermSearch,
  } = useForm();

  // --- Permission modal form ---
  const { control, handleSubmit, reset, setValue, register } = useForm({
    defaultValues: {
      user: null,
      can_view: false,
      can_export: false,
      can_filter: false,
      can_save_filter: false,
    },
  });

  const reportNameSearch = watchReportSearch('nameSearch');
  const permEmailSearch = watchPermSearch('emailSearch');

  // ---- User select with infinite scroll ----
  const USER_PAGE_SIZE = 50;
  const [userOptions, setUserOptions] = useState([]);
  const [userOffset, setUserOffset] = useState(0);
  const [userHasMore, setUserHasMore] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const userSearchQuery = useRef('');
  const userInputTimer = useRef(null);

  async function fetchUsers(query, offset) {
    setIsLoadingUsers(true);
    const res = await actions.searchUsers(query, offset, USER_PAGE_SIZE);
    const data = res?.data?.data || [];
    const total = res?.data?.meta?.total || 0;
    const mapped = data.map((u) => ({
      ...u,
      label: `${u.fullname || ''} (${u.email})`,
      value: u.id,
    }));
    setIsLoadingUsers(false);
    return { mapped, total };
  }

  async function onUserMenuOpen() {
    userSearchQuery.current = '';
    setUserOffset(0);
    setUserOptions([]);
    setUserHasMore(true);
    const { mapped, total } = await fetchUsers('', 0);
    setUserOptions(mapped);
    setUserHasMore(total > mapped.length);
    setUserOffset(USER_PAGE_SIZE);
  }

  function onUserInputChange(inputValue) {
    userSearchQuery.current = inputValue;
    clearTimeout(userInputTimer.current);
    userInputTimer.current = setTimeout(async () => {
      setUserOffset(0);
      setUserOptions([]);
      setUserHasMore(true);
      const { mapped, total } = await fetchUsers(inputValue, 0);
      setUserOptions(mapped);
      setUserHasMore(total > mapped.length);
      setUserOffset(USER_PAGE_SIZE);
    }, 300);
  }

  async function onUserMenuScrollToBottom() {
    if (!userHasMore || isLoadingUsers) return;
    const { mapped, total } = await fetchUsers(userSearchQuery.current, userOffset);
    setUserOptions((prev) => {
      const newOptions = [...prev, ...mapped];
      setUserHasMore(total > newOptions.length);
      return newOptions;
    });
    setUserOffset((prev) => prev + USER_PAGE_SIZE);
  }

  // ---- Report list callbacks ----
  const recallReports = async (fetchDataOptions) => {
    if (!checkForbidden(143)) return;
    setIsFetchingReports(true);
    await actions
      .getReports(
        { name: reportNameSearch },
        fetchDataOptions.pageIndex,
        fetchDataOptions.pageSize
      )
      .then((res) => {
        if (res?.status === 200) {
          setReports(res.data.data || []);
          setTotalReports(res.data.meta?.total || 0);
        }
      });
    setIsFetchingReports(false);
  };

  async function onSubmitReportSearch({ nameSearch }) {
    setIsFetchingReports(true);
    await actions
      .getReports(
        { name: nameSearch },
        0,
        localStorage.getItem('currentPageSizeStore') ?? 10
      )
      .then((res) => {
        if (res?.status === 200) {
          setReports(res.data.data || []);
          setTotalReports(res.data.meta?.total || 0);
        }
      });
    setRefreshReportPaging(true);
    setIsFetchingReports(false);
  }

  // ---- Permission list callbacks ----
  const recallPermissions = async (fetchDataOptions) => {
    if (!checkForbidden(143)) return;
    setIsFetchingPerms(true);
    await actions
      .getPermissionsByReport(
        selectedReport.id,
        fetchDataOptions.pageIndex,
        fetchDataOptions.pageSize
      )
      .then((res) => {
        if (res?.status === 200) {
          setPermissions(res.data.data || []);
          setTotalPermissions(res.data.meta?.total || 0);
        }
      });
    setIsFetchingPerms(false);
  };


  function selectReport(reportId) {
    history.push(`/admin/setting/user-report-permissions/${reportId}`);
  }

  // ---- Modal helpers ----
  function openCreateModal() {
    setIsCreate(true);
    reset({ user: null, can_view: false, can_export: false, can_filter: false, can_save_filter: false });
    onOpen();
  }

  function openEditModal(permId) {
    const perm = permissions.find((p) => p.id === permId);
    if (!perm) return;
    setIsCreate(false);
    setCurrentPermId(perm.id);
    const user = {
      id: perm.user_id,
      fullname: perm.user_fullname || perm.user_email,
      email: perm.user_email,
      label: `${perm.user_fullname || perm.user_email} (${perm.user_email})`,
      value: perm.user_id,
    };
    setValue('user', user);
    setValue('can_view', perm.can_view);
    setValue('can_export', perm.can_export);
    setValue('can_filter', perm.can_filter);
    setValue('can_save_filter', perm.can_save_filter);
    onOpen();
  }

  async function onSubmit(data) {
    try {
      if (isCreate) {
        await actions.createPermission({
          user_id: data.user.id,
          user_email: data.user.email,
          report_id: selectedReport.id,
          can_view: data.can_view,
          can_export: data.can_export,
          can_filter: data.can_filter,
          can_save_filter: data.can_save_filter,
        });
        toast({ description: 'Add permission successful', status: 'success', duration: 4000, isClosable: true, position: 'top-right' });
      } else {
        await actions.updatePermission(currentPermId, {
          can_view: data.can_view,
          can_export: data.can_export,
          can_filter: data.can_filter,
          can_save_filter: data.can_save_filter,
        });
        toast({ description: 'Update permission successful', status: 'success', duration: 4000, isClosable: true, position: 'top-right' });
      }
      onClose();
      // Reload permissions list
      recallPermissions({
        pageIndex: 0,
        pageSize: localStorage.getItem('currentPageSizeStore') ?? 10,
      });
    } catch (err) {
      toast({ description: err?.response?.data?.meta?.message || err.message, status: 'error', duration: 4000, isClosable: true, position: 'top-right' });
    }
  }


  // Đang chờ fetch report từ URL param — chưa render gì cả để tránh call API thừa
  if (report_id && !selectedReport) {
    return <Flex justify='center' align='center' h='200px'><Spinner /></Flex>;
  }

  // =============== STATE 2: Permissions of selected report ===============
  if (selectedReport) {
    return (
      <Card px='0px'>
        <CardHeader px='22px' mb='12px'>
          <Flex direction='column' display='block'>
            <Flex align='center' gap='8px' float='left'>
              <Button
                size='sm'
                variant='ghost'
                leftIcon={<ArrowBackIcon />}
                onClick={() => history.push('/admin/setting/user-report-permissions')}
                px='0'
              />
              <Text color={textColor} fontSize='lg' fontWeight='bold' mb='6px'>
                {selectedReport.name} — User Report Permissions
              </Text>
            </Flex>
            <Stack float='right' direction='row' spacing={4}>
              {checkForbidden(144) && (
                <Button
                  size='sm'
                  onClick={openCreateModal}
                  leftIcon={<AddIcon />}
                  colorScheme='blue'
                  variant='outline'
                >
                  Add User
                </Button>
              )}
            </Stack>

            {/* Modal create/edit permission */}
            <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose} size='md'>
              <ModalOverlay />
              <form onSubmit={handleSubmit(onSubmit)}>
                <ModalContent>
                  <ModalHeader>{isCreate ? 'Add User Permission' : 'Edit User Permission'}</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody pb={6}>
                    {isCreate && (
                      <FormControl mb='16px' isRequired>
                        <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                          User
                        </FormLabel>
                        <Controller
                          name='user'
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <ReactSelect
                              {...field}
                              chakraStyles={chakraStyles}
                              options={userOptions}
                              placeholder='Search user by email...'
                              getOptionLabel={(u) => u.label}
                              getOptionValue={(u) => u.id}
                              size='sm'
                              isClearable
                              isLoading={isLoadingUsers}
                              filterOption={() => true}
                              onMenuOpen={onUserMenuOpen}
                              onInputChange={onUserInputChange}
                              onMenuScrollToBottom={onUserMenuScrollToBottom}
                            />
                          )}
                        />
                      </FormControl>
                    )}
                    <FormControl>
                      <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                        Permissions
                      </FormLabel>
                      <Flex direction='column' gap='8px' ms='4px'>
                        <Checkbox {...register('can_view')}>View</Checkbox>
                        <Checkbox {...register('can_export')}>Export</Checkbox>
                        <Checkbox {...register('can_filter')}>Filter</Checkbox>
                        <Checkbox {...register('can_save_filter')}>Save Filter</Checkbox>
                      </Flex>
                    </FormControl>
                  </ModalBody>
                  <ModalFooter>
                    {(isCreate ? checkForbidden(144) : checkForbidden(145)) && (
                      <Button colorScheme='blue' mr={3} type='submit' size='sm'>
                        {isCreate ? 'Create' : 'Update'}
                      </Button>
                    )}
                    <Button onClick={onClose} size='sm'>Cancel</Button>
                  </ModalFooter>
                </ModalContent>
              </form>
            </Modal>
          </Flex>
        </CardHeader>
        <CardBody>
          <SearchTable2
            tableData={permissions}
            columnsData={columnsDataReportPermission}
            totalRow={totalPermissions}
            loadData={(fetchDataOptions) => recallPermissions(fetchDataOptions)}
            actionsActive={checkForbidden(145) ? ['edit'] : []}
            listPermissions={[6]}
            isFetching={isFetchingPerms}
            edit={openEditModal}
            enableSorting={false}
          />
        </CardBody>
      </Card>
    );
  }

  // =============== STATE 1: Report list ===============
  return (
    <Card px='0px'>
      <CardHeader px='22px' mb='12px'>
        <Flex direction='column' display='block'>
          <Text float='left' color={textColor} fontSize='lg' fontWeight='bold' mb='6px'>
            User Report Permissions
          </Text>
        </Flex>
      </CardHeader>
      <CardBody>
        <Card>
          <form onSubmit={handleSubmitReportSearch(onSubmitReportSearch)}>
            <Flex direction='row' alignItems='center' mb={3} gap={4} flexWrap='wrap'>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel ms='4px' fontSize='xs' fontWeight='semibold'>
                  Name
                </FormLabel>
                <Input
                  _placeholder={{ color: 'gray.400' }}
                  variant='main'
                  type='text'
                  placeholder='Report name'
                  minW='80px'
                  maxW='185px'
                  size='sm'
                  fontSize='sm'
                  {...registerReportSearch('nameSearch')}
                  _focus={{ borderColor: 'blue.500' }}
                />
              </Box>
            </Flex>
            <Flex justify='center' align='center' gap='8px' mt={3}>
              <Box direction='row' alignItems='center' display='block'>
                <Button
                  leftIcon={<RxReset />}
                  variant='primary'
                  w='88px'
                  h='33px'
                  onClick={() => resetReportSearch()}
                >
                  Reset
                </Button>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <Button leftIcon={<BsSearch />} variant='primary' w='88px' h='33px' type='submit'>
                  Search
                </Button>
              </Box>
            </Flex>
          </form>
        </Card>
        <SearchTable2
          tableData={reports}
          columnsData={columnsDataReport}
          totalRow={totalReports}
          loadData={(fetchDataOptions) => recallReports(fetchDataOptions)}
          actionsActive={['detail']}
          listPermissions={[]}
          isFetching={isFetchingReports}
          showDetail={selectReport}
          refreshPagingDefault={refreshReportPaging}
          enableSorting={false}
        />
      </CardBody>
    </Card>
  );
}

export default DataTableReportPermissions;
